import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { darkenHexColor, lightenHexColor } from "../../utilities/parsers";

// All measurements below are in the SVG's own design-space units (the
// original artwork was authored on a 320x140 canvas with a 22px label).
// Everything scales off of that reference.
const BASE_FONT_SIZE = 22;
const DESIGN_WIDTH = 320;
const DESIGN_HEIGHT = 140;
const TEXT_CENTER_X = 160;
const CONTENT_TOP = 22;

// The label reads small at the badge's native display size, so its glyphs are
// drawn boosted relative to BASE_FONT_SIZE - this only affects the text, the
// ticket artwork keeps scaling strictly off BASE_FONT_SIZE.
const TEXT_SIZE_BOOST = 1.35;
const TEXT_FONT_SIZE = BASE_FONT_SIZE * TEXT_SIZE_BOOST;

// How much horizontal room the inner plate offers a label before it needs
// to stretch, and how much breathing room to keep around the measured text.
const TEXT_SAFE_WIDTH = 200;
const SIDE_PADDING = 16;

// The badge only ever grows/shrinks in exact proportion to the configured
// font size (never distorts), clamped so a stray value can't blow up the layout.
const MIN_FONT_SCALE = 0.5;
const MAX_FONT_SCALE = 2.5;

// Native display size of the artwork at BASE_FONT_SIZE, before font scaling.
const DISPLAY_SCALE = 0.35;

// The whole ticket is built from exactly two seed colors - every other tone
// (gradient stops, strokes, shadows) is derived from these via lighten/darken
// so the artwork stays a coherent 2-color palette.
const DEFAULT_PRIMARY_COLOR = "#111111";
const DEFAULT_SECONDARY_COLOR = "#f36a0a";

const estimateTextWidth = (text: string) => text.length * TEXT_FONT_SIZE * 0.55;

type DiscountTicketBadgeProps = {
    text: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontStyle?: string;
    color?: string;
    primaryColor?: string;
    secondaryColor?: string;
    style?: CSSProperties;
    [dataAttr: `data-${string}`]: unknown;
};

const DiscountTicketBadge = ({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    color,
    primaryColor,
    secondaryColor,
    style,
    ...rest
}: DiscountTicketBadgeProps) => {
    const uid = useId();
    const textRef = useRef<SVGTextElement>(null);
    const [textWidth, setTextWidth] = useState(() => estimateTextWidth(text || ""));

    const measure = () => {
        const node = textRef.current;
        if (!node) return;
        try {
            const box = node.getBBox();
            if (box.width > 0) setTextWidth(box.width);
        } catch {
            // node not measurable yet (e.g. not laid out) - keep last known width
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    useEffect(() => {
        const fonts = (document as any)?.fonts;
        if (fonts?.ready) {
            fonts.ready.then(measure).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    if (!text) return null;

    const safeFontSize = fontSize && fontSize > 0 ? fontSize : BASE_FONT_SIZE;
    const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, safeFontSize / BASE_FONT_SIZE));

    const delta = Math.max(0, textWidth + SIDE_PADDING * 2 - TEXT_SAFE_WIDTH);
    const designWidth = DESIGN_WIDTH + delta;

    const finalScale = DISPLAY_SCALE * fontScale;
    const renderedWidth = designWidth * finalScale;
    const renderedHeight = DESIGN_HEIGHT * finalScale;
    const textX = TEXT_CENTER_X + delta / 2;

    const shadowId = `bf-shadow-${uid}`;
    const orangeId = `bf-orange-${uid}`;
    const bgId = `bf-bg-${uid}`;

    const primary = primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = secondaryColor || DEFAULT_SECONDARY_COLOR;

    // Neutral family: the outer silhouette, the inner plate, corner notches and fold line.
    const primaryLight = lightenHexColor(primary, 8);
    const primaryDark = darkenHexColor(primary, 4);

    // Accent family: the border gradient, the diamonds, and the underline flourish.
    const secondaryLight = lightenHexColor(secondary, 20);
    const secondaryDark = darkenHexColor(secondary, 15);

    return (
        <svg
            {...rest}
            width={renderedWidth}
            height={renderedHeight}
            viewBox={`0 0 ${designWidth} ${DESIGN_HEIGHT}`}
            style={{
                position: "absolute",
                top: -((CONTENT_TOP / DESIGN_HEIGHT) * renderedHeight + 16),
                right: -6,
                overflow: "visible",
                zIndex: 10,
                ...style,
            }}
            role="img"
            aria-label={text}
        >
            <defs>
                <linearGradient id={bgId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="50%" stopColor={primary} />
                    <stop offset="100%" stopColor={primaryDark} />
                </linearGradient>
                <linearGradient id={orangeId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={secondaryLight} />
                    <stop offset="45%" stopColor={secondary} />
                    <stop offset="100%" stopColor={secondaryDark} />
                </linearGradient>
                <filter id={shadowId} x="-20%" y="-30%" width="140%" height="180%">
                    <feDropShadow dx="0" dy="7" stdDeviation="4" floodColor="#000" floodOpacity=".6" />
                </filter>
            </defs>

            {/* Outer scalloped silhouette: the only part that stretches to fit the label */}
            <path
                d={`M25 30L45 22L55 30H${265 + delta}L${275 + delta} 22L${295 + delta} 30L${288 + delta} 42V100L${295 + delta} 112L${275 + delta} 120L${265 + delta} 112H55L45 120L25 112L32 100V42Z`}
                fill={primaryDark}
                stroke={primaryDark}
                strokeWidth={4}
                filter={`url(#${shadowId})`}
            />

            <path
                d={`M32 33L48 27L58 35H${262 + delta}L${272 + delta} 27L${288 + delta} 33L${282 + delta} 44V96L${288 + delta} 107L${272 + delta} 113L${262 + delta} 105H58L48 113L32 107L38 96V44Z`}
                fill={`url(#${orangeId})`}
            />

            <path d={`M45 39H${275 + delta}V101H45Z`} fill={`url(#${bgId})`} stroke={primaryDark} strokeWidth={4} />

            <path d={`M54 92H${266 + delta}`} stroke={primaryDark} strokeWidth={3} opacity={0.8} />

            {/* Corner notches: fixed ornaments anchored to the folded edges */}
            <path d="M25 52L32 44V96L25 88Z" fill={primaryDark} />
            <path d={`M${295 + delta} 52L${288 + delta} 44V96L${295 + delta} 88Z`} fill={primaryDark} />

            {/* Sparkle diamonds: pinned to the left and right ends, like justify-content: space-between */}
            <g fill={secondary} transform="translate(0 5)">
                <path d="M67 72L70 79L77 82L70 85L67 92L64 85L57 82L64 79Z" />
            </g>
            <g fill={secondary} transform={`translate(${delta} 5)`}>
                <path d="M253 72L256 79L263 82L256 85L253 92L250 85L243 82L250 79Z" />
            </g>

            <text
                ref={textRef}
                x={textX}
                y={72}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color || "#ffffff"}
                fontSize={TEXT_FONT_SIZE}
                fontWeight={fontWeight || 900}
                fontStyle={fontStyle || "normal"}
                letterSpacing={1}
            >
                {text}
            </text>

            {/* Underline flourish: recenters with the label instead of stretching */}
            <g transform={`translate(${delta / 2} 0)`}>
                <path d="M101 92H219" stroke={secondary} strokeWidth={2} strokeLinecap="round" />
                <path d="M92 92H82M228 92H238" stroke={secondaryLight} strokeWidth={2} strokeLinecap="round" />
            </g>
        </svg>
    );
};

export default DiscountTicketBadge;
